import { useCallback, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useCompetitorDna } from "@/hooks/use-creative-api";

function statusTone(completeness: "missing" | "partial" | "configured") {
  if (completeness === "configured") {
    return "text-[var(--color-success)]";
  }
  if (completeness === "partial") {
    return "text-[var(--color-warning)]";
  }
  return "text-[var(--color-destructive)]";
}

export default function CompetitorDna() {
  const [searchParams] = useSearchParams();
  const [url, setUrl] = useState("");
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const { data, loading, error, extract } = useCompetitorDna();
  const siteId = searchParams.get("siteId")?.trim() || undefined;

  const handleExtract = useCallback(() => {
    if (!url.trim()) {
      return;
    }

    setCopyState("idle");
    void extract(url.trim(), siteId ? { siteId } : undefined);
  }, [extract, siteId, url]);

  const handleCopyBrief = useCallback(async () => {
    if (!data?.recreationBrief) {
      return;
    }

    try {
      await navigator.clipboard.writeText(data.recreationBrief);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  }, [data?.recreationBrief]);

  const sectionSummary = useMemo(() => {
    return data?.designDna.sections ?? [];
  }, [data?.designDna.sections]);

  const handoffState = useMemo(() => {
    if (!data) {
      return null;
    }

    return {
      prompt: data.handoff.prompt,
      pageType: data.handoff.suggestedPageType,
      title: data.handoff.suggestedTitle,
      slug: data.handoff.suggestedSlug,
    };
  }, [data]);

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 p-6">
      <div className="rounded-2xl border border-border bg-background p-6 shadow-sm">
        <p className="text-xs font-medium uppercase tracking-[0.24em] text-[var(--color-ai)]">
          Mounted Seam
        </p>
        <h1 className="mt-2 text-3xl font-semibold text-foreground">Competitor DNA</h1>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">
          Extract layout and style patterns from a public HTTPS page, compare them against the
          workspace&apos;s canonical brand context, and copy a safe pattern-transfer brief for the
          mounted builder flow.
        </p>
        {siteId ? (
          <p className="mt-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Site-scoped theme context active
          </p>
        ) : null}
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.7fr_1fr]">
        <section className="rounded-2xl border border-border bg-background p-6 shadow-sm">
          <label htmlFor="competitor-url" className="text-sm font-medium text-foreground">
            Public page URL
          </label>
          <div className="mt-3 flex flex-col gap-3 sm:flex-row">
            <input
              id="competitor-url"
              type="url"
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              placeholder="https://competitor.com/landing-page"
              className="min-w-0 flex-1 rounded-xl border border-border bg-background px-4 py-3 text-sm text-foreground outline-none transition focus:border-[var(--color-ai)] focus:ring-2 focus:ring-[var(--color-ai)]/20"
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  handleExtract();
                }
              }}
            />
            <button
              onClick={handleExtract}
              disabled={loading || !url.trim()}
              className="rounded-xl bg-[var(--color-ai)] px-5 py-3 text-sm font-medium text-white transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? "Extracting..." : "Extract Patterns"}
            </button>
          </div>

          <div className="mt-4 rounded-xl border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/8 p-4 text-sm text-foreground">
            <p className="font-medium">Safety boundary</p>
            <p className="mt-1 text-muted-foreground">
              This seam extracts structure, spacing, palette, and typography only. It does not
              grant reuse of competitor copy, imagery, trademarks, or logos.
            </p>
          </div>

          {error ? (
            <div className="mt-4 rounded-xl border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/8 p-4 text-sm text-[var(--color-destructive)]">
              {error}
            </div>
          ) : null}

          {loading ? (
            <div className="mt-6 space-y-3 animate-pulse">
              <div className="h-24 rounded-2xl bg-muted" />
              <div className="h-32 rounded-2xl bg-muted" />
              <div className="h-40 rounded-2xl bg-muted" />
            </div>
          ) : null}

          {data ? (
            <div className="mt-6 space-y-6">
              <div className="rounded-2xl border border-border bg-muted/20 p-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">Extraction summary</p>
                    <p className="mt-1 text-sm text-muted-foreground">{data.sourceUrl}</p>
                  </div>
                  <div className="text-left sm:text-right">
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Confidence</p>
                    <p className="text-lg font-semibold text-foreground">
                      {Math.round(data.designDna.confidence * 100)}%
                    </p>
                  </div>
                </div>
                <div className="mt-4 grid gap-4 sm:grid-cols-3">
                  <MetricCard label="Layout" value={data.designDna.layoutPattern} />
                  <MetricCard label="Sections" value={String(sectionSummary.length)} />
                  <MetricCard label="Extracted" value={new Date(data.extractedAt).toLocaleString()} />
                </div>
              </div>

              <div className="grid gap-4 xl:grid-cols-2">
                <div className="rounded-2xl border border-border p-5">
                  <h2 className="text-sm font-semibold text-foreground">Extracted palette</h2>
                  <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {[
                      ["Primary", data.designDna.colors.primary],
                      ["Secondary", data.designDna.colors.secondary],
                      ["Accent", data.designDna.colors.accent],
                      ["Background", data.designDna.colors.background],
                      ["Text", data.designDna.colors.text],
                    ].map(([label, hex]) => (
                      <div key={label} className="rounded-xl border border-border p-3">
                        <div className="h-12 rounded-lg border border-border" style={{ backgroundColor: hex }} />
                        <p className="mt-2 text-xs font-medium text-foreground">{label}</p>
                        <p className="text-xs text-muted-foreground">{hex}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border border-border p-5">
                  <h2 className="text-sm font-semibold text-foreground">Typography and spacing</h2>
                  <dl className="mt-4 space-y-3 text-sm">
                    <InfoRow label="Heading font" value={data.designDna.typography.headingFont} />
                    <InfoRow label="Body font" value={data.designDna.typography.bodyFont} />
                    <InfoRow label="Body size" value={data.designDna.typography.bodySize} />
                    <InfoRow label="Section gap" value={data.designDna.spacing.sectionGap} />
                    <InfoRow label="Base unit" value={`${data.designDna.spacing.baseUnit}px`} />
                    <InfoRow label="Rhythm" value={data.designDna.spacing.rhythm} />
                  </dl>
                </div>
              </div>

              <div className="grid gap-4 xl:grid-cols-2">
                <div className="rounded-2xl border border-border p-5">
                  <h2 className="text-sm font-semibold text-foreground">Source context</h2>
                  <dl className="mt-4 space-y-3 text-sm">
                    <InfoRow label="Resolved URL" value={data.sourceContext.finalUrl} />
                    <InfoRow
                      label="Business"
                      value={data.sourceContext.brand.name ?? data.sourceContext.page.title ?? "Not detected"}
                    />
                    <InfoRow
                      label="Industry"
                      value={data.sourceContext.industryDetection
                        ? `${data.sourceContext.industryDetection.industry} (${Math.round(data.sourceContext.industryDetection.confidence * 100)}%)`
                        : "Not detected"}
                    />
                    <InfoRow
                      label="Logo"
                      value={data.sourceContext.brand.logoUrl ? "Detected" : "Not detected"}
                    />
                    <InfoRow
                      label="Social links"
                      value={String(data.sourceContext.brand.socialLinks.length)}
                    />
                  </dl>

                  {data.sourceContext.page.description ? (
                    <div className="mt-4 rounded-xl border border-border bg-muted/20 p-4">
                      <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
                        Source summary
                      </p>
                      <p className="mt-2 text-sm leading-6 text-foreground">
                        {data.sourceContext.page.description}
                      </p>
                    </div>
                  ) : null}
                </div>

                <div className="rounded-2xl border border-border p-5">
                  <h2 className="text-sm font-semibold text-foreground">Generator handoff</h2>
                  <dl className="mt-4 space-y-3 text-sm">
                    <InfoRow label="Suggested page type" value={data.handoff.suggestedPageType} />
                    <InfoRow label="Suggested title" value={data.handoff.suggestedTitle} />
                    <InfoRow label="Suggested slug" value={data.handoff.suggestedSlug} />
                  </dl>

                  {data.sourceContext.page.keywords.length > 0 ? (
                    <div className="mt-4 rounded-xl border border-border bg-muted/20 p-4">
                      <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
                        Source keywords
                      </p>
                      <p className="mt-2 text-sm text-foreground">
                        {data.sourceContext.page.keywords.join(", ")}
                      </p>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="rounded-2xl border border-border p-5">
                <h2 className="text-sm font-semibold text-foreground">Detected sections</h2>
                <div className="mt-4 space-y-3">
                  {sectionSummary.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No confident section mapping was detected from the fetched HTML.
                    </p>
                  ) : (
                    sectionSummary.map((section) => (
                      <div
                        key={`${section.order}-${section.sectionType}-${section.puckComponentType}`}
                        className="rounded-xl border border-border bg-muted/20 p-4"
                      >
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <p className="text-sm font-medium text-foreground">
                              {section.order + 1}. {section.sectionType}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Maps to <span className="font-medium text-foreground">{section.puckComponentType}</span>
                            </p>
                          </div>
                          <span className="text-xs font-medium text-muted-foreground">
                            {Math.round(section.confidence * 100)}% match
                          </span>
                        </div>
                        {section.contentHints.heading ? (
                          <p className="mt-2 text-xs text-muted-foreground">
                            Content hint: {section.contentHints.heading}
                          </p>
                        ) : null}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </section>

        <aside className="space-y-6">
          <section className="rounded-2xl border border-border bg-background p-6 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-foreground">Canonical style context</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  This uses the same brand context formatter the AI generation path consumes.
                </p>
              </div>
              <span className={`text-xs font-semibold uppercase tracking-[0.2em] ${statusTone(data?.styleContext.completeness ?? "missing")}`}>
                {data?.styleContext.completeness ?? "missing"}
              </span>
            </div>

            {data ? (
              <>
                <dl className="mt-4 space-y-3 text-sm">
                  <InfoRow label="Business" value={data.styleContext.businessName ?? "Not configured"} />
                  <InfoRow label="Voice" value={data.styleContext.brandVoice ?? "Not configured"} />
                  <InfoRow label="Heading font" value={data.styleContext.fonts.heading ?? "Not configured"} />
                  <InfoRow label="Body font" value={data.styleContext.fonts.body ?? "Not configured"} />
                  <InfoRow label="Primary color" value={data.styleContext.colors.primary ?? "Not configured"} />
                </dl>

                {data.styleContext.missingFields.length > 0 ? (
                  <div className="mt-4 rounded-xl border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/8 p-4 text-sm text-foreground">
                    <p className="font-medium">Still missing</p>
                    <p className="mt-1 text-muted-foreground">
                      {data.styleContext.missingFields.join(", ")}
                    </p>
                  </div>
                ) : null}

                <div className="mt-4 rounded-xl border border-border bg-muted/20 p-4">
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
                    AI Context Preview
                  </p>
                  <pre className="mt-2 whitespace-pre-wrap break-words text-xs leading-5 text-foreground">
                    {data.styleContext.promptContext || "No canonical brand context is configured yet."}
                  </pre>
                </div>
              </>
            ) : (
              <p className="mt-4 text-sm text-muted-foreground">
                Run an extraction to compare the competitor pattern with the current tenant style context.
              </p>
            )}
          </section>

          <section className="rounded-2xl border border-border bg-background p-6 shadow-sm">
            <p className="text-sm font-semibold text-foreground">Mounted next step</p>
            <p className="mt-2 text-sm text-muted-foreground">
              MIG-1 now hands the import context into the checkpoint-backed generator. Review the seeded prompt there, generate the first draft, then continue in the editor approval flow.
            </p>
            <div className="mt-4 flex flex-col gap-3">
              <Link
                to="/pages/streaming-generator"
                state={handoffState ?? undefined}
                className="rounded-xl bg-[var(--color-ai)] px-4 py-3 text-center text-sm font-medium text-white transition hover:opacity-95"
              >
                Open Generator with Import Context
              </Link>
              <button
                onClick={handleCopyBrief}
                disabled={!data?.recreationBrief}
                className="rounded-xl bg-foreground px-4 py-3 text-sm font-medium text-background transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {copyState === "copied"
                  ? "Brief Copied"
                  : copyState === "failed"
                    ? "Copy Failed"
                    : "Copy Pattern-Transfer Brief"}
              </button>
            </div>

            {data ? (
              <div className="mt-5 rounded-xl border border-border bg-muted/20 p-4">
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
                  Safety rules
                </p>
                <ul className="mt-2 space-y-2 text-sm text-foreground">
                  {data.safety.rules.map((rule) => (
                    <li key={rule}>{rule}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </section>
        </aside>
      </div>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-background p-4">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
      <p className="mt-2 text-sm font-medium text-foreground">{value}</p>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border/60 pb-3 last:border-b-0 last:pb-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium text-foreground">{value}</dd>
    </div>
  );
}
